
import { useState } from 'react';

const BookingSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    englishLevel: '',
    preferredTime: '',
    goals: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d47hl2ihchb23d4efo30', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          englishLevel: '',
          preferredTime: '',
          goals: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking-section" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Book Your Private Lesson
            </h2>
            <p className="text-xl text-gray-600">
              Ready to take your English to the next level? Fill out the form below and I'll get back to you 
              within 24 hours to schedule your personalized lesson.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Booking Form */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Lesson Booking Form</h3>
              
              <form id="private-lesson-booking" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="englishLevel" className="block text-sm font-medium text-gray-700 mb-2">
                      Current English Level *
                    </label>
                    <select
                      id="englishLevel"
                      name="englishLevel"
                      value={formData.englishLevel}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm pr-8"
                    >
                      <option value="">Select your level</option>
                      <option value="beginner">Beginner</option>
                      <option value="elementary">Elementary</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="upper-intermediate">Upper Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="not-sure">Not sure</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Lesson Time
                  </label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm pr-8"
                  >
                    <option value="">Select preferred time</option>
                    <option value="morning">Morning (9 AM - 12 PM)</option>
                    <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                    <option value="evening">Evening (5 PM - 8 PM)</option>
                    <option value="weekend">Weekend</option>
                    <option value="flexible">I'm flexible</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="goals" className="block text-sm font-medium text-gray-700 mb-2">
                    Learning Goals
                  </label>
                  <select
                    id="goals"
                    name="goals"
                    value={formData.goals}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm pr-8"
                  >
                    <option value="">What do you want to focus on?</option>
                    <option value="conversation">Conversation & Speaking</option>
                    <option value="pronunciation">Pronunciation</option>
                    <option value="grammar">Grammar</option>
                    <option value="business">Business English</option>
                    <option value="exam-prep">Exam Preparation</option>
                    <option value="job-interview">Job Interview Preparation</option>
                    <option value="travel">Travel English</option>
                    <option value="general">General Improvement</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
                    placeholder="Tell me more about your English learning goals or any specific areas you'd like to work on..."
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sending Request...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Book My Lesson (€20)
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-checkbox-circle-fill text-green-600 mr-2"></i>
                      <span className="text-green-700 font-medium">
                        Booking request sent successfully! I'll contact you within 24 hours.
                      </span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-error-warning-fill text-red-600 mr-2"></i>
                      <span className="text-red-700 font-medium">
                        Something went wrong. Please try again or contact me directly.
                      </span>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Lesson Details */}
            <div className="space-y-8">
              <div className="bg-indigo-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-indigo-900 mb-6">Lesson Details</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                      <i className="ri-time-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-indigo-900">Duration</h4>
                      <p className="text-indigo-700">60 minutes of focused, one-on-one instruction</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                      <i className="ri-money-euro-circle-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-indigo-900">Price</h4>
                      <p className="text-indigo-700">€20 per session - pay after the lesson</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                      <i className="ri-video-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-indigo-900">Platform</h4>
                      <p className="text-indigo-700">Video call via Zoom, Skype, or Google Meet</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                      <i className="ri-calendar-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-indigo-900">Scheduling</h4>
                      <p className="text-indigo-700">Flexible times including evenings and weekends</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-green-900 mb-4">What's Included</h3>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-green-800">Personalized lesson plan</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-green-800">Speaking practice & pronunciation tips</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-green-800">Grammar explanations & exercises</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-green-800">Homework & practice materials</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-green-800">Progress tracking & feedback</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
